import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger.js'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(ScrollToPlugin)

const elJumpLinkNav = document.querySelector('[data-jump-links]')
let isAnimating

/**
 * Remove active state from jump nav items
 */
const clearJumpLinkStates = () => {
    elJumpLinkNav.querySelectorAll('[data-jump-link]').forEach((elLink) => {
        elLink.removeAttribute('data-active')
    })
}

// Detect where actionBar is on the page
const elActionBar = document.querySelector('section.actionBar[data-jump-link-section][data-anchor]') // Select the action bar element

/**
 * Add active state to jump link
 * @param entries {array}
 */
const updateJumpLinks = (entries) => {
    const [entry] = entries
    if (entry.isIntersecting) {
        // only update if scrolling manually
        if (!isAnimating) {
            const targetLink = elJumpLinkNav.querySelector(
                `[data-jump-link="${entry.target.id}"]`
            )

            if (targetLink) {
                clearJumpLinkStates()
                targetLink.setAttribute('data-active', 'true')
            }
            if (history.pushState) {
                history.pushState(null, null, '#' + entry.target.id)
            } else {
                location.hash = entry.target.id
            }
        }
    }
}
// Adds hash updates to url for sections with anchor ids if there is no JumpLinkBar on the page.
const initNoJumpLinks = () => {
    const sections = [...document.querySelectorAll('[data-anchor]')].filter((el) => el.dataset.anchor)

    sections && sections.forEach((section) => {
        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries
            if (entry.isIntersecting) {
                if (history.pushState) {
                    history.pushState(null, null, '#' + entry.target.id)
                } else {
                    location.hash = entry.target.id
                }
            }
        }, {
            threshold: 0,
            rootMargin: '-70% 0% -30%',
        })

        observer.observe(section)
    })
}

const initJumpLinks = () => {
    const jumpLinkNavHeight = window.innerWidth > 850 ? elJumpLinkNav.clientHeight : 0
    const jumpLinks = elJumpLinkNav.getElementsByTagName('a')
    const sections = document.querySelectorAll('[data-jump-link-section]:not([id=""])')
    const jumpLinksTrigger = document.querySelector('[data-jump-links-trigger]')
    const jumpLinksContainer = document.querySelector('[data-jump-links-container]')

    elJumpLinkNav.style.setProperty('--jumplink-sections', sections.length)
    elJumpLinkNav.classList.remove('-autoColumns')
    elJumpLinkNav.classList.add('-customColumns')

    // Jumplink Accordion Setup ===================
    const updateJumplinksForMobile = () => {
        // If only one jump-link, keep 'desktop' layout
        if (elJumpLinkNav?.querySelectorAll('[data-jump-link]')?.length == 1) {
            return;
        } else {
            elJumpLinkNav.dataset.layout = 'mobile'
            elJumpLinkNav.setAttribute('aria-expanded', 'false')
            jumpLinksTrigger?.setAttribute('tabindex', 0)
            jumpLinksTrigger?.setAttribute('aria-hidden', 'false')

            jumpLinksTrigger?.addEventListener('click', onJumpLinksTriggerClick)
            jumpLinksContainer.addEventListener('click', onJumpLinksTriggerClick)
        }
    }

    const updateJumpLinksForDesktop = () => {
        elJumpLinkNav.dataset.layout = 'desktop'
        elJumpLinkNav.setAttribute('aria-expanded', 'true')
        jumpLinksTrigger?.setAttribute('tabindex', -1)
        jumpLinksTrigger?.setAttribute('aria-hidden', 'true')

        jumpLinksTrigger?.removeEventListener('click', onJumpLinksTriggerClick)
        jumpLinksContainer.removeEventListener('click', onJumpLinksTriggerClick)
    }

    const onJumpLinksTriggerClick = () => {
        if (jumpLinksTrigger !== null) {
            const isExpanded = elJumpLinkNav.getAttribute('aria-expanded')
            if (isExpanded === 'true') {
                elJumpLinkNav.setAttribute('aria-expanded', 'false')
                jumpLinksTrigger.dataset.active = 'false'
            } else {
                elJumpLinkNav.setAttribute('aria-expanded', 'true')
                jumpLinksTrigger.dataset.active = 'true'
            }
        }
    }

    const onJumplinksResize = () => {
        window.innerWidth < 1275
            ? updateJumplinksForMobile()
            : updateJumpLinksForDesktop()
    }

    // add events
    let timeout
    window.addEventListener('resize', () => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            onJumplinksResize()
        }, 250) // debounce resize so it doesn't run function on every change
    })

    // check viewport on load
    onJumplinksResize()

    /**
     * Handle scrolling to sections on jump nav item click
     */
    Array.from(jumpLinks).forEach((elJumpLink) => {
        elJumpLink.addEventListener('click', function (evt) {
            evt.preventDefault()
            history.pushState(
                {},
                window.title,
                `${elJumpLink.getAttribute('href')}`
            )
            clearJumpLinkStates()
            this.closest('li').setAttribute('data-active', 'true')
            isAnimating = true // prevent updating current section when animating scrolling

            // Get link's target section
            const jumpLinkTgt = elJumpLink?.parentNode?.getAttribute('data-jump-link');
            if (jumpLinkTgt) {
                const jumpLinkTgtEl = document.getElementById(jumpLinkTgt);
                if (jumpLinkTgtEl && jumpLinkTgtEl.classList.contains('accordionFeature')) {
                    // if target is accordion, close all others...
                    
                    /* 
                    // Just kidding... this throws off the animated scroll-down
                    // Leave this here but disabled, for now
                    for (let section of document.getElementsByClassName('layer accordionFeature')) {
                        for(let accordion of section.getElementsByClassName('accordion -outer')) {
                            accordion.setAttribute('data-open', 'false');
                        }
                    }*/

                    // ... and open the target
                    let targetAccordions = jumpLinkTgtEl.getElementsByClassName('accordion -outer');
                    for(let el of targetAccordions){ 
                        el.setAttribute('data-open', 'true')
                    };
                }
            }

            if (document.querySelector(elJumpLink.getAttribute('href'))) {
                gsap.to(window, {
                    duration: 1,
                    scrollTo: {
                        y: elJumpLink.getAttribute('href'),
                        offsetY: window.stickyHeight || jumpLinkNavHeight,
                    },
                    ease: 'power2',
                    onComplete: () => {
                        const elSection = document.getElementById(elJumpLink.getAttribute('href').replace('#', ''))
                        elSection.focus({ preventScroll: true });
                        isAnimating = false
                    },
                })
            }
        })
    })

    /**
     * Add observer to detect if the action bar is on top and add gap class
     */
    const initActionObserver = () => {
        const initialViewportHeight = window.innerHeight;
        const isDesktop = () => window.innerWidth > 850; // Adjust this value based on your breakpoint
    
        if (elActionBar) {
            console.log('action bar exists');
    
            window.addEventListener('scroll', () => {
                const scrollPosition = window.scrollY;
    
                if (scrollPosition > initialViewportHeight) {
                    elJumpLinkNav.classList.add('-gapOnTop');
                    
                    if (isDesktop()) {
                        elJumpLinkNav.style.top = '10.5rem'; // Only adjust top on desktop
                    } else {
                        elJumpLinkNav.style.top = '0'; // Reset top for mobile
                    }
    
                    console.log('Scrolled past viewport, class added.');
                } else {
                    elJumpLinkNav.classList.remove('-gapOnTop');
    
                    if (isDesktop()) {
                        elJumpLinkNav.style.top = '0'; // Reset to 0 for desktop
                    }
    
                    console.log('Scrolled back to the top, class removed.');
                }
            });
        }
    };

    // Call the observer initialization
    initActionObserver()

    /**
     * Update jump nav active states when new sections come into view
     */
    sections.forEach((section) => {
        const observer = new IntersectionObserver(updateJumpLinks, {
            threshold: 0,
            rootMargin: '-70% 0% -30%',
        })
        observer.observe(section)
    })

    const navObserver = new IntersectionObserver(([{ isIntersecting }]) => {
        if (isIntersecting && !isAnimating) {
            if (history.replaceState) {
                history.replaceState(null, null, ' ')
            } else {
                location.hash = null
            }
        }
    }, {
        threshold: 0,
        rootMargin: '-70% 0% -30%',
    })

    navObserver.observe(elJumpLinkNav)
}

(() => {
    setTimeout(() => {
        !elJumpLinkNav ? initNoJumpLinks() : initJumpLinks()
    }, 500)
})()
